import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormGroupDirective, NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CommonService } from '../../../../../services/common.service';
import { ErrorStateMatcher } from '@angular/material/core';
import { CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';

@Component({
    selector: 'app-time-select',
    templateUrl: './time-select.component.html',
    styleUrls: ['./time-select.component.scss', '../../../styles.scss'],
    standalone: false
})
export class TimeSelectComponent implements OnInit, OnDestroy {
  @ViewChild('input') input: ElementRef | undefined;

  @Input() label = '';
  /** A status pill after the label (e.g. "Autofilled", Figma Status badge 35281:21972); empty = none. */
  @Input() labelBadge = '';
  @Input() amPmControlName = 'am_pm';
  @Input() timePeriodControlName = 'timePeriod';
  @Input() focus = false;
  @Input() placeholder = '';
  @Input() parentFormGroup: FormGroup = new FormGroup<any>({});
  @Input() controlName = 'time';
  @Input() error = '';
  @Input() warning = '';
  @Input() hint = '';
  @Input() maxLength: string | number = 50;
  @Input() max = 9999999999;
  @Input() min = 0;
  @Input() prefixIcon = '';
  @Input() suffixIcon = '';
  @Input() suffixText = '';
  @Output() checkForErrors = new EventEmitter();
  labels = ['AM', 'PM'];

  fieldSubscription: Subscription | undefined;
  matcher = new CustomErrorStateMatcher();

  isRequired = false;
  isDisabled = false;
  menuOpen = false;

  overlayPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
  ];

  @ViewChild('trigger', { static: false }) trigger!: CdkOverlayOrigin;

  constructor(
    private commonService: CommonService,
    private cdRef: ChangeDetectorRef,
  ) {
  }

  // Do not show field as touched and having error when clicked outside the field
  @HostListener('focusout')
  onBlur() {
    if (!this.error) {
      this.parentFormGroup.controls[this.controlName].markAsUntouched();
    }
  }

  @HostListener('focusin')
  setInputFocus(): void {
    this.commonService.focusedIn.next(true);
  }

  ngOnInit(): void {
    const control = this.parentFormGroup?.controls[this.controlName];

    this.isDisabled = control?.status === 'DISABLED';

    this.fieldSubscription = control?.valueChanges.subscribe(() => {
      // If field is marked as touched, mark it as untouched
      if (this.parentFormGroup.controls[this.controlName]?.touched) {
        this.parentFormGroup.controls[this.controlName].markAsUntouched();
      }

      // Get errors on the form field
      const errors = this.parentFormGroup?.controls?.[this.controlName]?.errors;

      // If errors exist, handle it
      if (errors) {
        // Clear errors on the form field
        this.parentFormGroup.controls[this.controlName].setErrors(null);

        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);

        // Add errors again to the form field for showing messages in the future
        this.parentFormGroup.controls[this.controlName].setErrors(errors);
      } else {
        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);
      }
    });

    // Check if field is required
    if (control?.validator) {
      const validator = control?.validator({} as AbstractControl);
      this.isRequired = !!(validator && validator['required']);
    }

    this.cdRef.detectChanges();
  }

  toggleOverlay() {
    event.stopPropagation();
    this.menuOpen = !this.menuOpen;
  }

  closeOverlay() {
    const rawValue = this.parentFormGroup.getRawValue();
    let timePeriod = rawValue[this.timePeriodControlName];
    const amPm = rawValue[this.amPmControlName];


    if (timePeriod?.length === 3 && !timePeriod.includes(':')) {
      const hour = timePeriod.slice(0, 2).padStart(2, '0');
      let minute = Number(timePeriod[2]) * 10;

      if (minute > 59) {
        minute = 59;
      }

      timePeriod = `${hour}:${String(minute).padStart(2, '0')}`;
    }

    const hasAllFields = timePeriod && amPm;

    if (hasAllFields) {
      const combinedTime = `${timePeriod} ${amPm}`;
      this.parentFormGroup?.controls?.[this.controlName]?.setValue(combinedTime);
      this.parentFormGroup?.controls?.[this.timePeriodControlName]?.setValue(timePeriod);
      this.parentFormGroup?.controls?.[this.amPmControlName]?.setValue(amPm);
    } else {
      this.parentFormGroup?.controls?.[this.controlName]?.setValue(null);
      this.parentFormGroup?.controls?.[this.timePeriodControlName]?.setValue(null);
      this.parentFormGroup?.controls?.[this.amPmControlName]?.setValue(null);
    }

    this.menuOpen = false;
  }

  ngOnDestroy() {
    this.fieldSubscription?.unsubscribe();
  }
}

class CustomErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

}
